<script setup lang="ts">
  import { computed, getCurrentInstance, ref, toRef } from 'vue';
  import { useResizeObserver } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useDesignPreviewChannel } from '@/composition/design/use-design-preview-channel';
  import type { IDesignDraft } from '@/domain/design/design-draft';
  import { fitPhoneFrame, PHONE_VIEWPORT } from '@/domain/design/design-layout';
  import type { IPhoneFrameSize } from '@/domain/design/design-layout';
  import { buildPreviewMessage } from '@/domain/design/preview-message';

  /**
   * The real store in a phone (Storefront V2 ADM-3, D-ADM-2): the storefront
   * in preview mode inside a 390×844 iframe, scaled to fit the pane, restyled
   * by the draft through the CON-11 handshake without reloading. «Sin fotos»
   * hides the dish photos. It mounts before the saved look arrives (`draft`
   * null), so the store and the look load at the same time.
   */
  const props = defineProps<{
    src: string;
    draft: IDesignDraft | null;
    docked: boolean;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string) => String(proxy.$t(key));

  const stage = ref<HTMLElement | null>(null);
  const frame = ref<HTMLIFrameElement | null>(null);
  const hidePhotos = ref(false);
  /** The last measured fit (a closed sheet measures 0 and keeps it). */
  const size = ref<IPhoneFrameSize | null>(null);

  const message = computed(() =>
    props.draft ? buildPreviewMessage(props.draft, hidePhotos.value) : null
  );
  const { ready, failed, frameKey, reload } = useDesignPreviewChannel({
    frame,
    src: toRef(props, 'src'),
    message,
  });

  const photoItems = computed(() => [
    { id: 'with', label: t('design.preview.withPhotos') },
    { id: 'without', label: t('design.preview.withoutPhotos') },
  ]);

  useResizeObserver(stage, (entries) => {
    const box = entries[0]?.contentRect;
    if (!box) return;
    size.value = fitPhoneFrame(box.width, box.height) ?? size.value;
  });

  const phoneStyle = computed(() =>
    size.value
      ? { width: `${size.value.width}px`, height: `${size.value.height}px` }
      : undefined
  );
  const screenStyle = computed(() =>
    size.value
      ? {
          width: `${Math.round(PHONE_VIEWPORT.width * size.value.scale)}px`,
          height: `${Math.round(PHONE_VIEWPORT.height * size.value.scale)}px`,
        }
      : undefined
  );
  const frameStyle = computed(() =>
    size.value
      ? {
          transform: `scale(${size.value.scale})`,
          transformOrigin: '0 0',
        }
      : undefined
  );

  function setPhotos(id: string) {
    hidePhotos.value = id === 'without';
  }
</script>

<template>
  <div class="v2-design-preview" :class="{ 'is-docked': docked }">
    <div class="preview-bar">
      <h2 class="preview-title">{{ t('design.preview.title') }}</h2>
      <PosUiSegmented
        :value="hidePhotos ? 'without' : 'with'"
        :items="photoItems"
        @input="setPhotos"
      />
    </div>
    <div ref="stage" class="preview-stage">
      <div v-show="size" class="phone" :style="phoneStyle">
        <div class="phone-screen" :style="screenStyle">
          <iframe
            :key="frameKey"
            ref="frame"
            class="phone-frame"
            :src="src"
            :title="t('design.preview.frameTitle')"
            :width="PHONE_VIEWPORT.width"
            :height="PHONE_VIEWPORT.height"
            :style="frameStyle"
          />
          <div v-show="!ready && !failed" class="phone-overlay">
            <PosUiSkeleton class="overlay-skeleton" />
          </div>
          <div v-show="failed" class="phone-overlay is-error" role="alert">
            <p class="overlay-text">{{ t('design.preview.loadError') }}</p>
            <PosUiButton
              color="neutral"
              variant="outline"
              icon="refresh"
              @click="reload"
            >
              {{ t('design.retry') }}
            </PosUiButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-design-preview {
    @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-surface;

    &.is-docked {
      @apply tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200;
    }

    .preview-bar {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-px-4 tw-py-3;
    }

    .preview-title {
      @apply tw-m-0 tw-mr-3 tw-text-base tw-font-semibold tw-text-neutral-900;
    }

    .preview-stage {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-items-start tw-justify-center tw-px-4 tw-pb-4;
    }

    .phone {
      @apply tw-relative tw-flex-none tw-rounded-[28px] tw-bg-neutral-900;
      padding: 8px;
    }

    // The overlays cover the screen only: the bezel stays visible.
    .phone-screen {
      @apply tw-relative tw-overflow-hidden tw-rounded-[20px] tw-bg-surface;
    }

    .phone-frame {
      @apply tw-block tw-border-0;
    }

    .phone-overlay {
      @apply tw-absolute tw-left-0 tw-top-0 tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-bg-surface tw-p-6 tw-text-center;
    }

    .overlay-skeleton {
      @apply tw-h-full tw-w-full tw-rounded-xl;
    }

    .overlay-text {
      @apply tw-m-0 tw-mb-4 tw-text-sm tw-text-neutral-700;
    }
  }
</style>
